import { logger } from "@/utils/logger";
import i18n from "@/i18n";
import { FileContent, Metadata, QcHeader, ThirdType, UniformOdmName, receivedFile } from "@/types";
import { getParty } from "@/store/services/searchThird";
import { deepClone, doRef, groupByFn, headers, UON } from "@/utils";
import store from "@/store";
import { getCarProduct } from "@/store/services/financing/product";
import { api } from "@/auth/api";
import axios from "axios";
import { settings } from "@/settings";
import moment from "moment";
import Notify from 'quasar/src/plugins/Notify.js';;
import { getProcess, qualifyFile, receiveFile } from "@/store/services/task/actions/uploadDocumentService";
import { changeNbrReceeivedDoc } from "@/store/services/task/taskService";

const PRE_EXISTENT_DOC_MAX_CONCURRENCY = 4;
const PRE_EXISTENT_DOC_APPLY_CONCURRENCY = 2;
const FILE_METADATA_CACHE_TTL_MS = 2 * 60 * 1000;
// Une requete par tiers (tous types de documents) au lieu d'une par type, pendant UN chargement d'ecran.
const ENTITY_FILE_METADATA_QUERY_ID = "filemngt-metadata-target-entity-all";

/** Offre actuellement ouverte (Task ou detail de demande). */
const isOfferStillDisplayed = (offerUid?: string) =>
    !offerUid ||
    store.state.taskModule.offer?.resourceUid === offerUid ||
    store.state.middleOfficeModule.offer?.resourceUid === offerUid;

type FileMetadataQuery = {
    documentType: string,
    targetEntityObjectType: string,
    targetEntityUID: string,
    associatedOffer: string
};

type FileMetadataItem = {
    fileId: string,
    offerReference: string,
    targetId: string,
    documentType: string
};

const fileMetadataCache = new Map<string, { expireAt: number, data: FileMetadataItem[] }>();
const fileMetadataInFlight = new Map<string, Promise<FileMetadataItem[]>>();

const getFileMetadataCacheKey = (attr: FileMetadataQuery) =>
    [
        attr.documentType,
        attr.targetEntityObjectType,
        attr.targetEntityUID,
        attr.associatedOffer
    ].join("::");

const runWithConcurrencyLimit = async (tasks: Array<() => Promise<void>>, maxConcurrency: number) => {
    if (tasks.length === 0) return;
    let cursor = 0;
    const worker = async () => {
        while (cursor < tasks.length) {
            const currentIndex = cursor++;
            await tasks[currentIndex]();
        }
    };
    const poolSize = Math.max(1, Math.min(maxConcurrency, tasks.length));
    await Promise.all(Array.from({ length: poolSize }, () => worker()));
};

export const processDocuments = async (payload: {
    gatheringProcess: any,
    requiredDocuments: any,
    supportingDocuments: any[],
    receivedFiles: any[],
    /** resourceUid de l'offre pour laquelle ce chargement a ete lance (protection contre les reponses tardives). */
    contextOfferUid?: string
}) => {

    const {
        gatheringProcess,
        requiredDocuments,
        supportingDocuments,
        receivedFiles
    } = payload;
    const contextOfferUid =
        payload.contextOfferUid ||
        (store.state.taskModule.offer?.resourceUid as string) ||
        (store.state.middleOfficeModule.offer?.resourceUid as string);
    // L'utilisateur a deja ouvert une autre offre : ces documents ne la concernent pas.
    if (!isOfferStillDisplayed(contextOfferUid)) return;

    let processedDocuments: ProcessedDocument[] = [];
    const deleteManualDocs: ProcessedDocument[] = [];
    let ksiopFiles: any = [];
    const listKsiopDocuments: ProcessedDocument[] = [];
    let nbrTargetEntityNull = 0;

    const targetEntityMap = new Map(
        supportingDocuments.filter((doc: { targetEntity: TargetEntity }) => doc.targetEntity !== null)
            .map((doc: { targetEntity: TargetEntity }) => {
                return [doc.targetEntity.resourceUid, doc.targetEntity];
            })
    );

    for (const [key, value] of targetEntityMap) {
        const entity = await getTargetEntity(value);
        value.reference = entity.reference ? entity.reference : entity.name
        value.information = entity.firstName ? `${entity.firstName} ${entity.familyName}` : entity.name ? `${entity.name}` : '-'
        value.roles = entity.roles
        value.expanded = false
    }

    const setStatus = (status: { resourceUid: string }) => {
        return status.resourceUid
    }

    ksiopFiles = receivedFiles.filter(file =>
        file.content.some((doc: any) =>
            doc?.documentType?.resourceUid?.includes("FLUX_KSIOP")
        )
    );

    if (ksiopFiles.length > 0) {
        const latestKsiopFile = ksiopFiles.reduce((latest: any, current: any) => {
            const latestTs = latest?.receptionDate ? new Date(latest.receptionDate).getTime() : 0;
            const currentTs = current?.receptionDate ? new Date(current.receptionDate).getTime() : 0;
            return currentTs >= latestTs ? current : latest;
        });
        const receivedFile = latestKsiopFile;
        const processedDocument = new ProcessedDocument();
        processedDocument.typeJustify = "FLUX_KSIOP";
        processedDocument.documentType = receivedFile.content[0].documentType.resourceUid
        processedDocument.creationDate = moment(receivedFile.receptionDate).format("DD/MM/YYYY HH:mm");
        const content = receivedFile.content[0];
        if (content) {
            processedDocument.status = setStatus(content.status);
            processedDocument.comment = content.label;
        }
        processedDocument.receivedFileUid = receivedFile.resourceUid
        processedDocument.document = {
            originalFileName: content.label,
            resourceUid: receivedFile.fileReference.resourceUid,
            objectType: receivedFile.fileReference.objectType,
            systemUid: receivedFile.fileReference.systemUid,
            receivedFileUid: receivedFile.resourceUid
        };
        listKsiopDocuments.push(processedDocument)
    }

    for (const requiredDocument of requiredDocuments) {
        for (const supportingType of requiredDocument.supportingType) {
            const supDocumentsFiltered = supportingDocuments.filter((s: any) => s.documentFamily.resourceUid === supportingType.supportingDocument.resourceUid);
            for (const supportingDocument of supDocumentsFiltered) {
                if (supportingDocument && supportingDocument.targetEntity) {
                    const matchingReceivedFiles = findReceivedFile(supportingDocument.resourceUid, requiredDocument.resourceUid, receivedFiles);

                    if (matchingReceivedFiles && matchingReceivedFiles.length > 0) {
                        for (const receivedFile of matchingReceivedFiles) {
                            if (!receivedFile.content || receivedFile.content.length === 0) {
                                continue;
                            }

                            const content = receivedFile.content[0];

                            const processedDocument = new ProcessedDocument();
                            processedDocument.targetEntity = targetEntityMap.get(supportingDocument.targetEntity.resourceUid) as TargetEntity;
                            processedDocument.typeJustify = supportingDocument.documentFamily.resourceUid;
                            processedDocument.documentType = requiredDocument.resourceUid;
                            processedDocument.processResourceUid = gatheringProcess.resourceUid;
                            processedDocument.manual = supportingDocument.manual;
                            processedDocument.mandatory = supportingType.mandatory;

                            let pdStatus = '';
                            processedDocument.creationDate = moment(receivedFile.receptionDate).format("DD/MM/YYYY HH:mm:ss");

                            if (content) {
                                pdStatus = receivedFile.status.resourceUid;
                                processedDocument.status = setStatus(content.status);
                                processedDocument.comment = content.label;
                            }

                            processedDocument.supportingDocumentResourceUid = supportingDocument.resourceUid;
                            processedDocument.receivedFileUid = receivedFile.resourceUid;
                            processedDocument.document = {
                                originalFileName: content.label,
                                resourceUid: receivedFile.fileReference.resourceUid,
                                objectType: receivedFile.fileReference.objectType,
                                systemUid: receivedFile.fileReference.systemUid,
                                receivedFileUid: receivedFile.resourceUid,
                                comment: receivedFile.comment,
                                expirationDate: receivedFile.expirationDate,
                                netheosStatus: receivedFile.businessData?.statusNetheos
                            };
                            
                            const processedDocumentAlreadyAdded = processedDocuments.find((x: any) =>
                                x.receivedFileUid === processedDocument.receivedFileUid
                            );

                            const deleteManualDoc = deleteManualDocs.find((x: any) =>
                                x.receivedFileUid === processedDocument.receivedFileUid
                            );

                            processedDocument.addedBy =
                                receivedFile.businessData?.addedBy?.includes('@TWDDemo')
                                    ? 'Liz Fi'
                                    : receivedFile.businessData?.addedBy;

                            if (!processedDocumentAlreadyAdded) {
                                if (supportingDocument.manual) {
                                    if (pdStatus !== 'CANCELED' && pdStatus !== '') {
                                        processedDocuments.push(processedDocument);
                                    } else if (!deleteManualDoc) {
                                        deleteManualDocs.push(processedDocument);
                                    }
                                } else {
                                    processedDocuments.push(processedDocument);
                                }
                            }
                        }
                    } else {
                        const processedDocument = new ProcessedDocument();
                        processedDocument.targetEntity = targetEntityMap.get(supportingDocument.targetEntity.resourceUid) as TargetEntity;
                        processedDocument.typeJustify = supportingDocument.documentFamily.resourceUid;
                        processedDocument.documentType = requiredDocument.resourceUid;
                        processedDocument.processResourceUid = gatheringProcess.resourceUid;
                        processedDocument.manual = supportingDocument.manual;
                        processedDocument.mandatory = supportingType.mandatory;

                        const processedDocumentAlreadyAdded = processedDocuments.find((x: any) =>
                            x.targetEntity.resourceUid === processedDocument.targetEntity.resourceUid &&
                            x.typeJustify === processedDocument.typeJustify &&
                            x.documentType === processedDocument.documentType &&
                            !x.receivedFileUid
                        );

                        if (!processedDocumentAlreadyAdded) {
                            processedDocuments.push(processedDocument);
                        }
                    }
                } else if (supportingDocument && !supportingDocument.targetEntity) {
                    nbrTargetEntityNull = nbrTargetEntityNull + 1;
                }
            }
        }
    }

    let documentCount: { [key: string]: number } = {};
    processedDocuments.forEach(document => {
        documentCount[document.documentType] = (documentCount[document.documentType] || 0) + 1;
    });

    processedDocuments = processedDocuments.filter(document => {
        return documentCount[document.documentType] === 1 || document.creationDate !== "";
    });

    const processedDocumentsGrouped: Record<string, ProcessedDocument[]> | undefined = groupByFn(processedDocuments, (e: any) => {
        if (e.targetEntity.reference) return e.targetEntity.reference;
    });

    const parseTs = (s?: string) => {
        if (!s) return 0;
        const m = moment(s, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm"], true);
        return m.isValid() ? m.valueOf() : 0;
    };

    const processedDocumentsGroupedDedup: Record<string, ProcessedDocument[]> = Object.fromEntries(
        Object.entries(processedDocumentsGrouped || {}).map(([entityRef, docs]) => {
            const byPair = new Map<string, ProcessedDocument[]>();

            for (const d of docs) {
                const pairKey = `${d.typeJustify}::${d.documentType}`;
                
                if (!byPair.has(pairKey)) {
                    byPair.set(pairKey, []);
                }
                
                const existingDocs = byPair.get(pairKey)!;
                
                const isDuplicate = existingDocs.some(existing => 
                    existing.receivedFileUid && d.receivedFileUid && 
                    existing.receivedFileUid === d.receivedFileUid
                );
                
                if (isDuplicate) {
                    const existingIndex = existingDocs.findIndex(existing => 
                        existing.receivedFileUid === d.receivedFileUid
                    );
                    
                    if (existingIndex !== -1) {
                        const existing = existingDocs[existingIndex];
                        const dTs = parseTs(d.creationDate);
                        const existingTs = parseTs(existing.creationDate);
                        
                        const keepD =
                            dTs > existingTs ||
                            (dTs === existingTs && d.status === "QUALIFIED" && existing.status !== "QUALIFIED");
                        
                        if (keepD) {
                            existingDocs[existingIndex] = d;
                        }
                    }
                } else {
                    existingDocs.push(d);
                }
            }
            const allDocs = Array.from(byPair.values()).flat();
            allDocs.sort((a, b) => parseTs(b.creationDate) - parseTs(a.creationDate));
            
            return [entityRef, allDocs];
        })
    );

    const documents: GatheringDocument[] = [];

    const entityTargets = Object.entries(processedDocumentsGroupedDedup as Record<string, ProcessedDocument[]>)
        .map(([key, value], i) => {
            const entity = value.find(processedDocument => processedDocument.targetEntity.reference === key)?.targetEntity
            return {
                index: i++,
                reference: key,
                ...entity,
                documents: value
            }
        })

    const nbrDeleteManualDocs = deleteManualDocs.length ? deleteManualDocs.length : 0
    documents.push({
        entityTargets,
        nbrReceivedItems: '',
        nbrValidatedItems: '',
        requiredDocuments: requiredDocuments.length - nbrTargetEntityNull - nbrDeleteManualDocs,
        status: gatheringProcess.status.resourceUid,
        assessmentContext: gatheringProcess.assessmentContext
    })

    const offerReference =
        (store.state.taskModule.offer?.reference as string) ||
        (store.state.middleOfficeModule.offer?.reference as string);

    const preExistentDocsByMetadataKey = new Map<
        string,
        {
            attr: FileMetadataQuery;
            entries: Array<{ document: ProcessedDocument; gatheringProcess: any }>;
        }
    >();

    for (const document of documents) {
        if (!document.entityTargets) continue;
        for (const entityTarget of document.entityTargets) {
            for (const doc of entityTarget.documents) {
                if (!doc.receivedFileUid || !doc.document?.resourceUid) {
                    const targetEntityAttr: FileMetadataQuery = {
                        documentType: doc.documentType,
                        targetEntityObjectType: doc.targetEntity.objectType as string,
                        targetEntityUID: doc.targetEntity.resourceUid as string,
                        associatedOffer: offerReference,
                    };
                    const cacheKey = getFileMetadataCacheKey(targetEntityAttr);
                    const existing = preExistentDocsByMetadataKey.get(cacheKey);
                    if (existing) {
                        existing.entries.push({ document: doc, gatheringProcess: document });
                    } else {
                        preExistentDocsByMetadataKey.set(cacheKey, {
                            attr: targetEntityAttr,
                            entries: [{ document: doc, gatheringProcess: document }],
                        });
                    }
                }
            }
        }
    }

    // Portee locale a CE chargement : aucune reutilisation entre deux offres / deux ecrans.
    const entityMetadataRequests = new Map<string, Promise<FileMetadataItem[] | null>>();
    const getEntityMetadataForThisLoad = (attr: FileMetadataQuery) => {
        const entityKey = [attr.targetEntityObjectType, attr.targetEntityUID].join("::");
        let request = entityMetadataRequests.get(entityKey);
        if (!request) {
            request = getEntityFileMetadata(attr.targetEntityObjectType, attr.targetEntityUID);
            entityMetadataRequests.set(entityKey, request);
        }
        return request;
    };

    const preExistentDocTasks = Array.from(preExistentDocsByMetadataKey.values()).map(
        ({ attr, entries }) => () =>
            handlePreExistentDocumentsBatch({
                fileMetadataQuery: attr,
                entries,
                offerReference,
                entityMetadata: getEntityMetadataForThisLoad(attr),
            })
    );

    const addedByTasks: Array<() => Promise<void>> = [];
    for (const document of documents) {
        for (const entityTarget of document.entityTargets || []) {
            for (const doc of entityTarget.documents) {
                const fileId: string | undefined = doc.document?.resourceUid;
                if (doc.receivedFileUid && fileId && !doc.addedBy) {
                    addedByTasks.push(async () => {
                        doc.addedBy = await getFileAddedBy(fileId, offerReference).catch(() => '') || '';
                    });
                }
            }
        }
    }

    if (!isOfferStillDisplayed(contextOfferUid)) return;

    store.dispatch("taskModule/gatheringProcess", {
        request: {
            gatheringProcess: documents,
        },
    }).catch(e => logger.error(e));

    store.dispatch("taskModule/KsiopDocuments", {
        request: {
            listKsiopDocuments: listKsiopDocuments,
        },
    }).catch(e => logger.error(e));

    store.dispatch("taskModule/setNbrReceivedDocuments");

    if (preExistentDocTasks.length > 0) {
        runWithConcurrencyLimit(preExistentDocTasks, PRE_EXISTENT_DOC_MAX_CONCURRENCY)
            .then(() => {
                if (!isOfferStillDisplayed(contextOfferUid)) return;
                store.dispatch("taskModule/gatheringProcess", {
                    request: {
                        gatheringProcess: [...documents],
                    },
                }).catch(e => logger.error(e));
                store.dispatch("taskModule/setNbrReceivedDocuments");
            })
            .catch(e => logger.error(e));
    }

    if (addedByTasks.length > 0) {
        runWithConcurrencyLimit(addedByTasks, PRE_EXISTENT_DOC_MAX_CONCURRENCY)
            .then(() => {
                if (!isOfferStillDisplayed(contextOfferUid)) return;
                store.dispatch("taskModule/gatheringProcess", {
                    request: {
                        gatheringProcess: [...documents],
                    },
                }).catch(e => logger.error(e));
            })
            .catch(e => logger.error(e));
    }
}

const applyPreExistentDocumentMetadata = async (attr: {
    document: ProcessedDocument;
    gatheringProcess: any;
    fileMetadata: FileMetadataItem[];
    offerReference: string;
}) => {
    const { document: originalDocument, gatheringProcess, fileMetadata, offerReference } = attr;
    const fileMetadataMap = new Map<string, FileMetadataItem[]>();

    for (const item of fileMetadata) {
        if (!fileMetadataMap.has(item.documentType)) {
            fileMetadataMap.set(item.documentType, []);
        }
        fileMetadataMap.get(item.documentType)!.push(item);
    }

    const metadataList = fileMetadataMap.get(originalDocument.documentType);
    const system = i18n.global.t("task.supportingDocumentLabel.system");

    if (metadataList && metadataList.length > 0) {
        const matchingDocuments = metadataList.filter(
            (metadata: FileMetadataItem) =>
                metadata.offerReference === offerReference &&
                metadata.documentType === originalDocument.documentType
        );

        if (matchingDocuments.length > 0) {
            let metadata = matchingDocuments.find(
                (m: FileMetadataItem) => m.fileId === originalDocument.document?.resourceUid
            );

            if (!metadata) {
                metadata = matchingDocuments[0];
            }

            const metadataMap = new Map<string, string>();
            metadataMap.set("associatedOffer", metadata.offerReference);

            if (metadata && metadata.offerReference !== offerReference) {
                const fileId = metadata.fileId;
                metadataMap.set("addedBy", `${offerReference};${system}`);
                metadataMap.forEach((value, key) => {
                    addMetadata(fileId, { key, value });
                });

                originalDocument.addedBy = system;
            } else {
                const connectedUser = store.getters["authModule/getUserConnected"];
                const addedByValue = (metadata as any).addedBy
                    ? (metadata as any).addedBy
                    : `${connectedUser.firstName} ${connectedUser.lastName}`;
                originalDocument.addedBy = addedByValue;
            }

            originalDocument.document.resourceUid = metadata.fileId;

            try {
                await store.dispatch("taskModule/uploadDocumentModule/downloadFile", {
                    request: originalDocument.document,
                    row: originalDocument,
                    callback: (response: any) => {
                        delete response.content;
                        handleResponse({
                            response,
                            gatheringProcess,
                            document: originalDocument,
                        });
                    },
                });
            } catch (error) {
                logger.error("Error in downloadFile:", error);
            }
        }
    }

    if (!originalDocument.receivedFileUid) {
        await receiveQualifySystemFile(originalDocument);
    }
};

const handlePreExistentDocumentsBatch = async (attr: {
    fileMetadataQuery: FileMetadataQuery;
    entries: Array<{ document: ProcessedDocument; gatheringProcess: any }>;
    offerReference: string;
    entityMetadata?: Promise<FileMetadataItem[] | null>;
}) => {
    const allForEntity = attr.entityMetadata ? await attr.entityMetadata : null;
    const fileMetadata: FileMetadataItem[] = allForEntity
        ? filterEntityFileMetadata(allForEntity, attr.fileMetadataQuery)
        // Repli : comportement historique (une requete par type de document)
        : await getFileMetadata(attr.fileMetadataQuery);
    const applyTasks = attr.entries.map(({ document, gatheringProcess }) => () =>
        applyPreExistentDocumentMetadata({
            document,
            gatheringProcess,
            fileMetadata,
            offerReference: attr.offerReference,
        })
    );
    await runWithConcurrencyLimit(applyTasks, PRE_EXISTENT_DOC_APPLY_CONCURRENCY);
};

export const handlePreExistentDocumentInLoading = async (attr: {
    document: ProcessedDocument,
    gatheringProcess: any
}) => {
    const { document: originalDocument, gatheringProcess } = attr;
    const offerReference = store.state.taskModule.offer?.reference as string || store.state.middleOfficeModule.offer?.reference as string;

    const targetEntityAttr: FileMetadataQuery = {
        documentType: originalDocument.documentType,
        targetEntityObjectType: originalDocument.targetEntity.objectType as string,
        targetEntityUID: originalDocument.targetEntity.resourceUid as string,
        associatedOffer: offerReference
    };

    const fileMetadata = await getFileMetadata(targetEntityAttr);
    await applyPreExistentDocumentMetadata({
        document: originalDocument,
        gatheringProcess,
        fileMetadata,
        offerReference,
    });
};
async function receiveQualifySystemFile(document: any) {
    getProcess(document.processResourceUid).then((res: any) => {
        res.gatheringProcessItem.forEach((element: any) => {
            const file = {
                fileReference: {
                    objectType: "odm.filemngt.filedescriptor",
                    systemUid: "odm-filemngt",
                    resourceUid: document.document.resourceUid
                },
                creationLabel: document.document.originalFileName,
                qualification: [
                    {
                        documentType: {
                            resourceUid: document.documentType
                        },
                        label: document.document.originalFileName
                    }
                ],
                targetEntityId: document.targetEntity.resourceUid,
                systemUid: 'odm-supportingdocument'
            }
            receiveFile(document.processResourceUid, element.resourceUid, file).then(res => {
                document.receivedFileUid = res?.resourceUid
                qualifyFile(document.processResourceUid, element.resourceUid, res?.resourceUid, file)
            })
        });
    });
}

const handleResponse = (attr: { response: any, gatheringProcess: any, document: ProcessedDocument }) => {

    const { response, gatheringProcess, document } = attr;

    document.creationDate = document.creationDate ? document.creationDate : moment(new Date()).format("DD/MM/YYYY HH:mm");
    document.document = response || {};
    document.comment = response.originalFileName;

    if (!document.status) {
        document.status = i18n.global.t(
            `task.uploadDocument.headers.supportingDocumentStatus.RECEIVED`
        );
    }

}

export const uploadDocument = (attr: { document: ProcessedDocument, files: FileList, gatheringProcess: any, metadataMap: Map<string, string>, documentReceivedFileID?: string }) => {
    const { document, files, gatheringProcess, metadataMap, documentReceivedFileID } = attr;
    document.document = files[0];
    
    if (document.document) {
        const fileContent: FileContent = {
            originalFileName: document.document.name,
            format: document.document.type,
            content: document.document,
            relations: {
                items: [
                    {
                        qualifier: {
                            rawValue: document.documentType,
                        },
                        objectUON: {
                            objectType: UON.ObjectType.fileManagement.fileContent,
                            systemUid: UON.SystemUid.odmFileManagement,
                        },
                    },
                ],
            },
            metadata: getMetadata(metadataMap),
        };

        store.dispatch("taskModule/uploadDocumentModule/uploadFile", {
            request: fileContent,
            document: document,
            documentType: document.documentType,
            processid: document.processResourceUid,
            documentReceivedFileID: documentReceivedFileID,
            callback: (response: FileContent) =>
                handleResponse({
                    response,
                    gatheringProcess,
                    document
                }),
        }).then(() => changeNbrReceeivedDoc('INCREMENT')).catch(e => logger.error(e));
        
    } else {
        Notify.create({
            timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
            color: "negative",
            message: "Erreur!",
        });
    }
}

const getBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const resp = reader.result as string;
            resolve(resp.split("base64,")[1]);
        };
        reader.onerror = (error) => reject(error);
    });
}

const getMetadata = (metaDataMap: Map<string, string>) => {
    return Array.from(metaDataMap.entries()).map(([key, value]) => ({
        key, value
    }) as Metadata);
}

const getTargetEntity = async (targetEntity: TargetEntity) => {
    if (targetEntity.objectType?.toLowerCase().includes('person')) {
        return (await getParty(ThirdType.PERSON, targetEntity.resourceUid as string)).response;
    }
    if (targetEntity.objectType?.toLowerCase().includes('organization')) {
        return (await getParty(ThirdType.ORGANIZATION, targetEntity.resourceUid as string)).response;
    }
    return (await getCarProduct(targetEntity.resourceUid as string));
}

/**
 * Meme filtre que la requete filemngt-metadata-target-entity :
 * (documentType = type demande) OU (associatedOffer = offre courante), sur le tiers deja filtre cote serveur.
 */
export const filterEntityFileMetadata = (allForEntity: FileMetadataItem[], query: FileMetadataQuery): FileMetadataItem[] =>
    allForEntity
        .filter((item) => item.documentType === query.documentType || item.offerReference === query.associatedOffer)
        .map((item) => ({ ...item }));

/** Tous les documents d'un tiers. null si la requete est indisponible (repli sur la requete par type). */
const getEntityFileMetadata = async (
    targetEntityObjectType: string,
    targetEntityUID: string
): Promise<FileMetadataItem[] | null> => {
    try {
        // axios direct (comme thirdService) : en cas d'echec, pas de notification d'erreur a l'utilisateur,
        // on bascule silencieusement sur la requete historique.
        const { data } = await axios.get(settings.api_query_url, {
            headers: headers({
                qc: JSON.stringify({
                    queryId: ENTITY_FILE_METADATA_QUERY_ID,
                    offset: 0,
                    limit: -1,
                    parameters: { targetEntityObjectType, targetEntityUID },
                }),
            }),
        });
        if (!Array.isArray(data)) return null;
        return data.map((el: { file_id: string, offer_reference: string, target_entity: string, document_type: string }) => ({
            fileId: el.file_id,
            offerReference: el.offer_reference,
            targetId: el.target_entity,
            documentType: el.document_type,
        }));
    } catch (e) {
        logger.warn(`${ENTITY_FILE_METADATA_QUERY_ID} indisponible : repli sur la requete par type de document`);
        return null;
    }
};

export const getFileMetadata = async (attr: FileMetadataQuery) => {
    const cacheKey = getFileMetadataCacheKey(attr);
    const now = Date.now();
    const cached = fileMetadataCache.get(cacheKey);
    if (cached && cached.expireAt > now) {
        return cached.data;
    }
    const inFlight = fileMetadataInFlight.get(cacheKey);
    if (inFlight) {
        return inFlight;
    }

    const { documentType, targetEntityObjectType, targetEntityUID, associatedOffer } = attr;
    const qc_header: QcHeader = {
        qc: {
            queryId: "filemngt-metadata-target-entity",
            offset: 0,
            limit: -1,
            parameters: {
                documentType, targetEntityObjectType, targetEntityUID, associatedOffer
            }
        }
    };

    const request = (async () => {
        const { data } = (await api().get(settings.api_query_url, {
            headers: headers({ qc: JSON.stringify(qc_header.qc) })
        }));

        const mapped = data.map((el: { file_id: string, offer_reference: string, target_entity: string, document_type: string }) => ({
            fileId: el.file_id,
            offerReference: el.offer_reference,
            targetId: el.target_entity,
            documentType: el.document_type
        }));
        fileMetadataCache.set(cacheKey, {
            expireAt: Date.now() + FILE_METADATA_CACHE_TTL_MS,
            data: mapped
        });
        return mapped;
    })().finally(() => {
        fileMetadataInFlight.delete(cacheKey);
    });

    fileMetadataInFlight.set(cacheKey, request);
    return request;
}

const getFileAddedBy = async (fileId: string, offerReference: string) => {
    const systemUid = UON.SystemUid.odmFileManagement
    const baseUrl = `${settings.api_url}/${systemUid}/api/1/${systemUid}`;
    let metadata: Metadata[];
    try {
        const { data } = await api().get(`${baseUrl}/filedescriptor/${fileId}/filemetadata/`);
        metadata = Array.isArray(data) ? data : data?.items || [];
    } catch {
        const { data } = await api().get(`/${systemUid}/api/1/${systemUid}/file/${fileId}/`);
        metadata = data?.metadata || [];
    }
    const addedByItems = metadata.filter((m: Metadata) => m.key === 'addedBy' && m.value);
    const item = addedByItems.find((m: Metadata) => m.value.split(";")[0] === offerReference) || addedByItems[0];
    if (item) return item.value.split(";")[1];
    const associatedOffer = metadata.find((m: Metadata) => m.key === 'associatedOffer')?.value;
    return associatedOffer && associatedOffer !== offerReference ? 'Liz Fi' : '';
}

export const addMetadata = (fileId: string, metadata: Metadata) => {
    // Les documents ont change : le cache court ne doit plus servir d'anciennes metadonnees.
    fileMetadataCache.clear();
    const systemUid = UON.SystemUid.odmFileManagement
    const URI = `${settings.api_url}/${systemUid}/api/1/${systemUid}/filedescriptor/${fileId}/filemetadata/`;
    api().post(URI, metadata).catch(e => {
        logger.error(e);
        throw e;
    });
}


export const deleteMetadataFile = async (fileId: string) => {
    fileMetadataCache.clear();
    const systemUid = UON.SystemUid.odmFileManagement
    const URI = `${settings.api_url}/${systemUid}/api/1/${systemUid}/filemetadata/${fileId}/`;
    return new Promise<receivedFile>(async (resolve, reject) => {
        try {
            const result = await api().delete(URI)
            resolve(result.data)
        } catch (e) {
            reject(e)
        }
    });
}

export const deleteReceivedFile = (processUid: string, fileId: string) => {
    const systemUid = UON.SystemUid.odmSupportingDocument
    const URI = `${settings.api_url}/${systemUid}/api/1/${systemUid}/process/${processUid}/file/${fileId}/`;
    api().delete(URI).catch(e => {
        logger.error(e);
        throw e;
    });
}

export const overwriteFile = async (file: any, processUid: string, fileId: string, docId: string) => {
    const systemUid = UON.SystemUid.odmSupportingDocument
    const baseUrl = `${settings.api_url}/${systemUid}/api/1/${systemUid}/`
    const body = file
    return new Promise<receivedFile>(async (resolve, reject) => {
        try {
            const result = await api().put(`${baseUrl}/process/` + processUid + `/document/` + docId + `/file/` + fileId + `/`, body)
            resolve(result.data)
        } catch (e) {
            reject(e)
        }
    });
}

export const findReceivedFile = (supportingDocumentUid: string, requiredDocumentUid: string, receivedFiles: any[]) => {
    return receivedFiles
        .filter((file: any) => 
            file.content && 
            file.content.length > 0 && 
            file.content[0].documentType?.resourceUid === requiredDocumentUid
        )
        .filter((file: any) => 
            file.content[0].qualification && 
            file.content[0].qualification.length > 0
        )
        .filter((file: any) => 
            file.content[0].qualification.some((qual: any) => 
                qual.supportingDocument.resourceUid === supportingDocumentUid
            )
        )
};

export type GatheringDocument = {
    entityTargets?: Array<{ reference: string, documents: ProcessedDocument[] }>
    nbrReceivedItems: string,
    nbrValidatedItems?: string,
    requiredDocuments?: number,
    status?: string,
    assessmentContext?: string
}

export type TargetEntity = {
    reference: string
    information: string
    type: string
    roles: never[],
    expanded: boolean
} & UniformOdmName

type UploadDocument = {
    originalFileName?: string,
    name?: string,
    type?: string,
    receivedFileUid?: string,
    metadata?: any,
    comment?:string,
    expirationDate?:any,
    netheosStatus?: any
} & UniformOdmName

type FilelMetadata = {
    fileId: string
    offerReference: string
    targetId: string
    documentType: string
    addedBy?: string
}

export class ProcessedDocument {
    reference: string;
    targetEntity: TargetEntity;
    typeJustify: string;
    documentType: string;
    creationDate: string;
    status: string;
    comment: string;
    processResourceUid: string;
    addedBy: string;
    document: UploadDocument
    supportingDocumentResourceUid: string;
    receivedFileUid: string;
    manual: string;
    mandatory: boolean;

    constructor() {
        this.reference = doRef(2);
        this.targetEntity = {} as TargetEntity;
        this.typeJustify = '';
        this.documentType = '';
        this.creationDate = '';
        this.status = '';
        this.comment = '';
        this.processResourceUid = '';
        this.addedBy = '';
        this.document = {} as UploadDocument;
        this.supportingDocumentResourceUid = '';
        this.receivedFileUid = '';
        this.manual = '';
        this.mandatory = false;
    }
}
